import { formatNumberLocale } from './util/number-format-locale'

// --------------------------------------------------------------------------------------------- //

/*
 * Locales code
 * https://gist.github.com/raushankrjha/d1c7e35cf87e69aa8b4208a8171a8416
 */

export type InputNumberValue = string | number | null | undefined

type Options = Intl.NumberFormatOptions | undefined

const DEFAULT_LOCALE = { code: 'en-US', currency: 'USD' }

// --------------------------------------------------------------------------------------------- //

function processInput(inputValue: InputNumberValue): number | null {
  if (inputValue == null || Number.isNaN(inputValue)) return null
  return Number(inputValue)
}

// --------------------------------------------------------------------------------------------- //

export function fNumber(inputValue: InputNumberValue, options?: Options) {
  const locale = formatNumberLocale() || DEFAULT_LOCALE

  const number = processInput(inputValue)
  if (number === null) return ''

  const defaultOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8
  } as Options

  const mergedOptions = { ...defaultOptions, ...options } as Options

  if (mergedOptions.maximumFractionDigits < mergedOptions.minimumFractionDigits) {
    mergedOptions.maximumFractionDigits = mergedOptions.minimumFractionDigits
  }

  const fm = new Intl.NumberFormat(locale.code, mergedOptions).format(number)

  return fm
}

// --------------------------------------------------------------------------------------------- //

export function fCurrency(inputValue: InputNumberValue, options?: Options) {
  const locale = formatNumberLocale() || DEFAULT_LOCALE

  const number = processInput(inputValue)
  if (number === null) return ''

  const defaultOptions = {
    style: 'currency',
    currency: locale.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  } as Options

  const mergedOptions = { ...defaultOptions, ...options } as Options

  if (mergedOptions.maximumFractionDigits < mergedOptions.minimumFractionDigits) {
    mergedOptions.maximumFractionDigits = mergedOptions.minimumFractionDigits
  }

  const fm = new Intl.NumberFormat(locale.code, mergedOptions).format(number)

  return fm
}

// --------------------------------------------------------------------------------------------- //

export function fCryptocurrency(inputValue: InputNumberValue, code?: string, options?: Options) {
  const defaultOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8
  } as Options

  const mergedOptions = { ...defaultOptions, ...options } as Options

  const resultRaw = fNumber(inputValue, mergedOptions)

  if (!code) {
    return resultRaw
  }

  return `${resultRaw} ${code?.toUpperCase()}`
}

// --------------------------------------------------------------------------------------------- //

export function fPercent(percentValue: InputNumberValue, options?: Options) {
  const locale = formatNumberLocale() || DEFAULT_LOCALE

  const number = processInput(percentValue)

  if (number === null) return ''

  const defaultOptions: Options = {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 2
  }

  const mergedOptions = { ...defaultOptions, ...options }

  if (mergedOptions.maximumFractionDigits < mergedOptions.minimumFractionDigits) {
    mergedOptions.maximumFractionDigits = mergedOptions.minimumFractionDigits
  }

  // const fm = new Intl.NumberFormat(locale.code, mergedOptions).format(number / 100)
  const fm = new Intl.NumberFormat(locale.code, mergedOptions).format(number / 100)

  return fm
}

// --------------------------------------------------------------------------------------------- //

export function fShortenNumber(inputValue: InputNumberValue, options?: Options) {
  const locale = formatNumberLocale() || DEFAULT_LOCALE

  const number = processInput(inputValue)
  if (number === null) return ''

  const defaultOptions = {
    notation: 'compact',
    maximumFractionDigits: 2
  } as Options

  const mergedOptions = { ...defaultOptions, ...options } as Options

  if (mergedOptions.maximumFractionDigits < mergedOptions.minimumFractionDigits) {
    mergedOptions.maximumFractionDigits = mergedOptions.minimumFractionDigits
  }

  const fm = new Intl.NumberFormat(locale.code, mergedOptions).format(number)

  // return fm.replace(/[A-Z]/g, match => match.toLowerCase())
  return fm
}

// --------------------------------------------------------------------------------------------- //

export function fData(inputValue: InputNumberValue) {
  const number = processInput(inputValue)
  if (number === null || number === 0) return '0 bytes'

  const units = ['bytes', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb']
  const decimal = 2
  const baseValue = 1024

  const index = Math.floor(Math.log(number) / Math.log(baseValue))
  const fm = `${parseFloat((number / baseValue ** index).toFixed(decimal))} ${units[index]}`

  return fm
}
